import { Form, Row, Col, Button, Space, Popconfirm } from "antd";
import { useTranslation } from "react-i18next";
import AdnDropdown from "../common/adnDropdown/AdnDropdown";
import AdnSwitch from "../common/adnSwitch/AdnSwitch";
import AdnInput from "../common/adnInput/AdnInput";
import moment from "moment";
import AdnListComponent from "../common/adnListComponent/AdnListComponent";
import AdnDatePicker from "../common/adnDatePicker/AdnDatePicker";
import {
  AnswerType,
  AnsweredByOptions,
  QuizStatus,
  dateFormat,
} from "../common/GeneralConfigurations";
import AdnCard from "../common/adnCard/AdnCard";
import "./QuizBuilderForm.css";
import { ArrowDown, ArrowUp, Edit2, X } from "react-feather";
import AdnTextArea from "../common/adnTextArea/AdnTextArea";

const QuestionForm = ({
  index,
  questionNameValidation,
  answerTypeValidation,
  handleDeleteQuestion,
  handleCancelQuestion,
  handleSaveQuestion,
  handleMoveUp,
  handleMoveDown,
  handleQuestionNameChange,
  handleQuestionAnswerTypeChange,
  handleQuestionMaxValue,
  handleQuestionMinValue,
  questionGroup,
  answerTypeOptions,
  answerOption,
  showQuestionButtons,
  questionForm,
  questionsByGroupId,
  handleAddChoices,
  choiceOptions,
  questionDomainOptions,
  quiz,
  handleChangeAnsweredBy,
  question,
  handleQuestionMinValueDatePicker,
  handleQuestionMaxValueDatePicker,
  editQuestion,
  setEditQuestion,
  setQuestionGroup,
}) => {
  const { t } = useTranslation();

  const isFirstQuestion =
    questionsByGroupId.findIndex((q) => q.id === questionGroup.id) === 0;
  const isLastQuestion =
    questionsByGroupId.findIndex((q) => q.id === questionGroup.id) ===
    questionsByGroupId.length - 1;

  const minValueValidation = [
    (questionGroup.answerTypeId === AnswerType.Escala ||
      questionGroup.answerTypeId === AnswerType.EscolhaMultipla) && {
      required: true,
      message: t("questionForm.validation.minValueRequired"),
    },
    {
      validator: (_, value, callback) => {
        if (
          questionGroup.answerTypeId === AnswerType.Escala &&
          value &&
          !/^\d+(\.\d+)?$/.test(value)
        ) {
          callback(t("questionForm.validation.validIntegerOrDecimal"));
        }
        if (
          questionGroup.answerTypeId === AnswerType.EscolhaMultipla &&
          value &&
          (!Number.isInteger(parseFloat(value)) || parseInt(value) <= 1)
        ) {
          callback(t("questionForm.validation.validIntegerGreaterThan1"));
        }
        if (
          questionGroup.answerTypeId === AnswerType.EscolhaMultipla &&
          value &&
          choiceOptions.length <= parseInt(value)
        ) {
          callback(t("questionForm.validation.numberGreaterThanChoiceOptions"));
        }
        callback();
      },
    },
  ];

  const maxValueValidation = [
    questionGroup.answerTypeId === AnswerType.Escala && {
      required: true,
      message: t("questionForm.validation.maxValueRequired"),
    },
    {
      validator: (_, value, callback) => {
        if (
          (questionGroup.answerTypeId === AnswerType.Escala ||
            questionGroup.answerTypeId === AnswerType.EscolhaMultipla) &&
          value &&
          !/^\d+(\.\d+)?$/.test(value)
        ) {
          callback(t("questionForm.validation.validIntegerOrDecimal"));
        }
        if (
          value &&
          parseFloat(value) <=
            parseFloat(questionForm.getFieldValue("minValue"))
        ) {
          callback(t("questionForm.validation.maxValueGreaterThanMin"));
        }
        callback();
      },
    },
  ];

  const miDateValueValidation = [
    {
      validator: (_, value, callback) => {
        if (
          questionGroup.answerTypeId === AnswerType.Data &&
          value &&
          !moment(value, "DD-MM-YYYY", true).isValid()
        ) {
          callback(t("questionForm.validation.invalidDate"));
        } else if (
          questionGroup.answerTypeId === AnswerType.Data &&
          value &&
          moment(value, "DD-MM-YYYY").isAfter(
            moment(questionGroup.maxValue, "DD-MM-YYYY")
          )
        ) {
          callback(t("questionForm.validation.minDateLessThanMax"));
        }
        callback();
      },
    },
  ];

  const maxDateValueValidation = [
    {
      validator: (_, value, callback) => {
        if (
          questionGroup.answerTypeId === AnswerType.Data &&
          value &&
          !moment(value, "DD-MM-YYYY", true).isValid()
        ) {
          callback(t("questionForm.validation.invalidDate"));
        } else if (
          questionGroup.answerTypeId === AnswerType.Data &&
          value &&
          moment(value, "DD-MM-YYYY").isBefore(
            moment(questionGroup.minValue, "DD-MM-YYYY")
          )
        ) {
          callback(t("questionForm.validation.maxDateGreaterThanMin"));
        }
        callback();
      },
    },
  ];

  const genExtra = (event) => (
    <Row gutter={16}>
      {showQuestionButtons && quiz.status !== QuizStatus.Published && (
        <Col>
          {!isFirstQuestion && (
            <Popconfirm
              title={t("questionForm.confirmations.changeQuestionOrder")}
              onConfirm={() => handleMoveUp(event)}
            >
              <Button
                className="question-updownbutton"
                size="small"
                title={t("questionForm.buttons.moveUp")}
              >
                <ArrowUp />
              </Button>
            </Popconfirm>
          )}
          {!isLastQuestion && (
            <Popconfirm
              title={t("questionForm.confirmations.changeQuestionOrder")}
              onConfirm={() => handleMoveDown(event)}
            >
              <Button
                className="question-updownbutton"
                size="small"
                title={t("questionForm.buttons.moveDown")}
              >
                <ArrowDown />
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title={t("questionForm.confirmations.deleteQuestion")}
            onConfirm={() => handleDeleteQuestion(event)}
          >
            <Button
              type="link"
              danger
              size="small"
              title={t("questionForm.buttons.delete")}
            >
              <X />
            </Button>
          </Popconfirm>
        </Col>
      )}
    </Row>
  );

  const handleEditQuestion = () => {
    setEditQuestion(true);
  };

  const handleChangeAnsweredByName = (id) => {
    var a = AnsweredByOptions.find((x) => x.id === id).name;
    return a;
  };

  const handleAllowNotApplicable = () => {
    setQuestionGroup((prevQuestionGroup) => ({
      ...prevQuestionGroup,
      allowNotApplicable: !prevQuestionGroup.allowNotApplicable,
    }));
  };

  const handleIsRequired = () => {
    setQuestionGroup((prevQuestionGroup) => ({
      ...prevQuestionGroup,
      isRequired: !prevQuestionGroup.isRequired,
    }));
  };

  return (
    <>
      <AdnCard>
        <div className="question">
          {editQuestion ? (
            <Form
              form={questionForm}
              name="questionForm"
              layout="vertical"
              onFinish={handleSaveQuestion}
              initialValues={questionGroup ? questionGroup : []}
            >
              <Row className="gutter-row" gutter={16}>
                <AdnInput
                  name="id"
                  type={"hidden"}
                  initialValues={questionGroup.id}
                />
                <Col span={24}>
                  <AdnTextArea
                    label={t("questionForm.labels.question")}
                    name="name"
                    type="text"
                    placeholder={t("questionForm.placeholders.newQuestion")}
                    maxLength={200}
                    initialValues={questionGroup && questionGroup.name}
                    handleChange={handleQuestionNameChange}
                    rules={questionNameValidation}
                    disabled={quiz.status === QuizStatus.Published}
                    autoSize
                  />
                </Col>
                <Col>
                  <Row gutter={16}>
                    <Col>
                      <AdnSwitch
                        name="isRequired"
                        label={t("questionForm.labels.required")}
                        checkedText={t("questionForm.buttonsText.yes")}
                        unCheckedText={t("questionForm.buttonsText.no")}
                        defaultChecked={questionGroup.isRequired}
                        disabled={quiz.status === QuizStatus.Published}
                        onChange={handleIsRequired}
                      />
                    </Col>
                    <Col>
                      <AdnSwitch
                        name="allowNotApplicable"
                        label={t("questionForm.labels.allowNotApplicable")}
                        checkedText={t("questionForm.buttonsText.yes")}
                        unCheckedText={t("questionForm.buttonsText.no")}
                        defaultChecked={questionGroup.allowNotApplicable}
                        disabled={quiz.status === QuizStatus.Published}
                        onChange={handleAllowNotApplicable}
                      />
                    </Col>
                    {quiz.isAnsweredByConsultant ? (
                      <Col className="question-answertype">
                        <AdnDropdown
                          name="answeredBy"
                          label={t("questionForm.labels.answeredBy")}
                          placeholder={t(
                            "questionForm.placeholders.selectAnsweredBy"
                          )}
                          rules={[
                            {
                              required: true,
                              message: t(
                                "questionForm.validation.answeredByRequired"
                              ),
                            },
                          ]}
                          options={AnsweredByOptions}
                          initialValues={
                            question &&
                            AnsweredByOptions.find(
                              (x) => x.id === question.answeredBy
                            ).name
                          }
                          handleChange={handleChangeAnsweredBy}
                          disabled={quiz.status === QuizStatus.Published}
                          allowClear
                        />
                      </Col>
                    ) : null}
                  </Row>
                </Col>
                <Col className="question-answertype">
                  <AdnDropdown
                    label={t("questionForm.labels.answerType")}
                    name="answerTypeId"
                    placeholder={t(
                      "questionForm.placeholders.selectAnswerType"
                    )}
                    handleChange={handleQuestionAnswerTypeChange}
                    rules={answerTypeValidation}
                    initialValues={answerOption}
                    options={answerTypeOptions}
                    disabled={quiz.status === QuizStatus.Published}
                  />
                </Col>
                <Row>
                  {(questionGroup.answerTypeId === AnswerType.Escala ||
                    questionGroup.answerTypeId ===
                      AnswerType.EscolhaMultipla) && (
                    <Col>
                      <AdnInput
                        name="minValue"
                        type="text"
                        label={
                          questionGroup.answerTypeId ===
                          AnswerType.EscolhaMultipla
                            ? t("questionForm.labels.requiredChoiceOptions")
                            : t("questionForm.labels.minimumValue")
                        }
                        placeholder={
                          questionGroup.answerTypeId ===
                          AnswerType.EscolhaMultipla
                            ? t(
                                "questionForm.placeholders.requiredChoiceOptions"
                              )
                            : t("questionForm.placeholders.minimumValue")
                        }
                        initialValues={questionGroup.minValue}
                        handleChange={handleQuestionMinValue}
                        maxLength={20}
                        rules={minValueValidation}
                        disabled={quiz.status === QuizStatus.Published}
                      />
                    </Col>
                  )}
                  {questionGroup.answerTypeId === AnswerType.Data && (
                    <Col>
                      <AdnDatePicker
                        name="minValue"
                        label={t("questionForm.labels.minimumDate")}
                        placeholder={t("questionForm.placeholders.minimumDate")}
                        defaultPickerValue={
                          questionGroup && questionGroup.minValue
                        }
                        onChange={(date, dateString) =>
                          handleQuestionMinValueDatePicker(date, dateString)
                        }
                        rules={miDateValueValidation}
                        disabled={quiz.status === QuizStatus.Published}
                        format={dateFormat}
                        value={questionGroup && questionGroup.minValue}
                      />
                    </Col>
                  )}
                  &nbsp;
                  {questionGroup.answerTypeId === AnswerType.Escala && (
                    <Col>
                      <AdnInput
                        name="maxValue"
                        type="text"
                        label={t("questionForm.labels.maximumValue")}
                        placeholder={t(
                          "questionForm.placeholders.maximumValue"
                        )}
                        initialValues={questionGroup.maxValue}
                        handleChange={handleQuestionMaxValue}
                        maxLength={20}
                        rules={maxValueValidation}
                        disabled={quiz.status === QuizStatus.Published}
                      />
                    </Col>
                  )}
                  {questionGroup.answerTypeId === AnswerType.Data && (
                    <Col>
                      <AdnDatePicker
                        name="maxValue"
                        label={t("questionForm.labels.maximumDate")}
                        placeholder={t("questionForm.placeholders.maximumDate")}
                        defaultPickerValue={
                          questionGroup && questionGroup.maxValue === undefined
                            ? ""
                            : questionGroup.maxValue
                        }
                        onChange={(date, dateString) =>
                          handleQuestionMaxValueDatePicker(date, dateString)
                        }
                        rules={maxDateValueValidation}
                        disabled={quiz.status === QuizStatus.Published}
                        format={dateFormat}
                        value={questionGroup && questionGroup.maxValue}
                      />
                    </Col>
                  )}
                </Row>
              </Row>

              {(questionGroup.answerTypeId === AnswerType.EscolhaUnica ||
                questionGroup.answerTypeId === AnswerType.EscolhaMultipla) && (
                <Row gutter={16}>
                  <Col span={16}>
                    <AdnListComponent
                      name={"choiceOptions"}
                      label={t("questionForm.labels.choiceOptions")}
                      size="small"
                      renderItem={(item) => <span>{item.text}</span>}
                      onListChange={handleAddChoices}
                      data={questionDomainOptions}
                      disabled={quiz.status === QuizStatus.Published}
                      maxLength={200}
                    />
                  </Col>
                </Row>
              )}

              {quiz.status !== QuizStatus.Published && (
                <Row className="gutter-row" justify="end" width="100%">
                  <Col>
                    <Space>
                      <Popconfirm
                        title={t("questionForm.confirmations.cancelChanges")}
                        onConfirm={() => handleCancelQuestion(questionGroup)}
                        okText={t("questionForm.buttonsText.yes")}
                        cancelText={t("questionForm.buttonsText.no")}
                      >
                        <Button className="adn-outlined-button outlined-cancel">
                          {t("questionForm.buttons.cancel")}
                        </Button>
                      </Popconfirm>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="adn-primary-button"
                      >
                        {t("questionForm.buttons.save")}
                      </Button>
                    </Space>
                  </Col>
                </Row>
              )}
            </Form>
          ) : (
            <Row>
              <Col xs={24} sm={24} md={24} lg={20} xl={20} xxl={20}>
                <Row gutter={12}>
                  <Col xs={24} className="question-title">
                    {/* questionGroup.description is the name when the value name is updated */}
                    <AdnTextArea
                      name="name"
                      type="text"
                      placeholder={
                        questionGroup.description
                          ? questionGroup.description
                          : questionGroup.name
                      }
                      disabled
                      autoSize
                    />
                  </Col>
                  <Col>
                    <Row gutter={8}>
                      {questionGroup.isRequired && (
                        <Col>
                          <p
                            style={{
                              margin: 0,
                              color: "rgb(155,32,32,0.8)",
                              fontWeight: "bold",
                            }}
                          >
                            {t("questionForm.display.required")}
                          </p>
                        </Col>
                      )}
                      {questionGroup.isRequired &&
                        questionGroup.allowNotApplicable && (
                          <Col>
                            <p
                              style={{
                                margin: 0,
                                color: "var(--adngrey)",
                                fontWeight: "bold",
                              }}
                            >
                              |
                            </p>
                          </Col>
                        )}
                      {questionGroup.allowNotApplicable && (
                        <Col>
                          <p
                            style={{
                              margin: 0,
                              color: "rgba(119, 206, 255, 0.8)",
                              fontWeight: "bold",
                            }}
                          >
                            {t("questionForm.display.allowedNotApplicable")}
                          </p>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
                <Row gutter={8}>
                  {quiz.isAnsweredByConsultant ? (
                    <Col>
                      <p
                        style={{
                          margin: 0,
                          color: "var(--adngrey)",
                        }}
                      >
                        {t("questionForm.display.answeredBy")}{" "}
                        <b>
                          {handleChangeAnsweredByName(questionGroup.answeredBy)}
                        </b>
                      </p>
                    </Col>
                  ) : (
                    ""
                  )}
                  <Col>
                    <p
                      style={{
                        margin: 0,
                        color: "var(--adngrey)",
                      }}
                    >
                      {t("questionForm.display.answeredType")}{" "}
                      <b>{answerOption}</b>
                    </p>
                  </Col>
                </Row>

                {/* the value 3 and 4 are the question types with defined answers */}
                {(questionGroup.answerTypeId === 3 ||
                  questionGroup.answerTypeId === 4) && (
                  <Row className="question-answers-group">
                    {questionDomainOptions.map((item) => (
                      <Col xl={24}>
                        <p key={item.id} className="question-answers">
                          {item.text}
                        </p>
                      </Col>
                    ))}
                  </Row>
                )}

                {/* the value 5 is the date type and it must show the min and max values */}
                {questionGroup.answerTypeId === 5 && (
                  <Row className="question-answers-group" gutter={16}>
                    <Col>
                      <p className="question-answers">
                        {t("questionForm.display.from")}{" "}
                        {questionGroup.minValue._i}
                      </p>
                    </Col>
                    <Col>
                      <p className="question-answers">
                        {t("questionForm.display.to")}{" "}
                        {questionGroup.maxValue._i}
                      </p>
                    </Col>
                  </Row>
                )}

                {/* the value 6 is the range type and it must show the min and max values */}
                {questionGroup.answerTypeId === 6 && (
                  <Row className="question-answers-group" gutter={16}>
                    <Col>
                      <p className="question-answers">
                        {t("questionForm.display.from")}{" "}
                        {questionGroup.minValue}
                      </p>
                    </Col>
                    <Col>
                      <p className="question-answers">
                        {t("questionForm.display.to")} {questionGroup.maxValue}
                      </p>
                    </Col>
                  </Row>
                )}
              </Col>
              {quiz.status !== QuizStatus.Published && (
                <Col xs={24} sm={24} md={24} lg={4} xl={4} xxl={4}>
                  <Row gutter={12} justify="end">
                    <Col>
                      <Edit2
                        onClick={() => handleEditQuestion()}
                        color="var(--adnblue)"
                      />
                    </Col>
                    <Col>{genExtra(questionGroup)}</Col>
                  </Row>
                </Col>
              )}
            </Row>
          )}
        </div>
      </AdnCard>
    </>
  );
};

export default QuestionForm;
